/**
 * Article body — sections/article-body.liquid
 *
 * Two jobs, both of which need the rendered prose and neither of which CSS can
 * do on its own:
 *
 * 1. The reading-progress bar the designer's note (Figma 5326:6494) asks for.
 *    Progress is measured against the prose column, not the section, so the
 *    commerce rail's height never moves the bar.
 * 2. Putting `role="list"` back on the body's bullet lists. The design's 6px dot
 *    needs `list-style: none`, which removes the list role in Safari/VoiceOver,
 *    and the markup comes from the rich-text editor so the attribute cannot be
 *    written into it.
 */

const SCROLL_LISTENER_OPTIONS = {
  passive: true,
};

if (!customElements.get('article-body')) {
  customElements.define(
    'article-body',
    class ArticleBody extends HTMLElement {
      prose = null;
      bar = null;
      frame = 0;

      /**
       * Scroll fires far more often than the bar can usefully repaint, so the
       * layout read is deferred to the next animation frame and coalesced.
       * A class field, so the reference stays stable for removeEventListener.
       */
      onScroll = () => {
        if (this.frame) {
          return;
        }

        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };

      connectedCallback() {
        this.prose = this.querySelector('[data-article-body-prose]');
        this.bar = this.querySelector('[data-article-body-progress-bar]');

        this.restoreListSemantics();

        // No bar means the merchant turned the progress indicator off.
        if (!this.prose || !this.bar) {
          return;
        }

        window.addEventListener('scroll', this.onScroll, SCROLL_LISTENER_OPTIONS);
        window.addEventListener('resize', this.onScroll, SCROLL_LISTENER_OPTIONS);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);

        if (this.frame) {
          cancelAnimationFrame(this.frame);
          this.frame = 0;
        }
      }

      update() {
        const rect = this.prose.getBoundingClientRect();

        // Clamped to 1 so a prose column shorter than the viewport still fills
        // the bar rather than dividing by zero or a negative distance.
        const travel = Math.max(rect.height - window.innerHeight, 1);
        const progress = Math.min(Math.max(-rect.top / travel, 0), 1);

        this.style.setProperty('--article-body-progress', progress.toFixed(4));
      }

      restoreListSemantics() {
        if (!this.prose) {
          return;
        }

        for (const list of this.prose.querySelectorAll(':scope > ul')) {
          list.setAttribute('role', 'list');
        }
      }
    },
  );
}
